<template>
  <div class="device-details">
    <div v-if="selectedDevice">
      <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
        <b-breadcrumb-item tag="a" @click.prevent="$router.push({name: 'device-list'})">
          {{ $t('global.devices') }}
        </b-breadcrumb-item>
        <b-breadcrumb-item tag="span" active>
          <InlineNameEditor
            :model-value="selectedDevice?.name || id"
            :can-edit="!!selectedDevice"
            :placeholder="$t('devices.name')"
            :save="saveDeviceName"
          />
        </b-breadcrumb-item>
      </b-breadcrumb>

      <div class="columns">
        <div class="column is-7">
          <div class="box">
            <b-tabs v-model="activeTab" expanded>
              <b-tab-item :label="$t('devices.events')">
                <EventsStreamTable :topics="deviceEventTopics" :stripPrefix="deviceEventsPrefix" :maxHeight="eventsTableHeight" size="is-small" />
              </b-tab-item>
              <b-tab-item label="Vitals">
                <div class="field is-grouped is-grouped-right mb-3">
                  <p class="control">
                    <b-button class="is-primary is-small" @click="loadStats" :loading="statsLoading" :disabled="statsLoading" icon-left="reload" :title="$t('global.refresh')"/>
                  </p>
                  <p class="control">
                    <b-select size="is-small" v-model="window" @change="loadStats">
                      <option value="1h">{{$t('charts.last_1h')}}</option>
                      <option value="1d">{{$t('charts.last_1d')}}</option>
                      <option value="7d">{{$t('charts.last_7d')}}</option>
                      <option value="15d">{{$t('charts.last_15d')}}</option>
                      <option value="30d">{{$t('charts.last_30d')}}</option>
                    </b-select>
                  </p>
                </div>
                <Line ref="rssiChart" :data="rssiData" :options="rssiOptions" width="100%" />
                <Line ref="heapChart" :data="heapData" :options="heapOptions" width="100%" />
              </b-tab-item>
            </b-tabs>
          </div>
        </div>
        <div class="column is-5">
          <div class="box">
            <h3 class="title is-5">{{ $t('devices.details') }}</h3>
            <table class="table is-fullwidth">
              <tbody>
                <tr>
                  <td><strong>{{ $t('devices.name') }}:</strong></td>
                  <td>
                    <div class="is-flex is-align-items-center">
                      <InlineNameEditor
                        :model-value="selectedDevice?.name || id"
                        :can-edit="!!selectedDevice"
                        :placeholder="$t('devices.name')"
                        :save="saveDeviceName"
                        show-copy
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td><strong>{{ $t('devices.id') }}:</strong></td>
                  <td>
                    <div class="is-flex is-align-items-center">
                      <span>{{ selectedDevice.id }}</span>
                      <CopyToClipboard :value="selectedDevice.id" title="Copy"/>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td><strong>{{ $t('devices.model') }}:</strong></td>
                  <td>{{ selectedDevice.model }}</td>
                </tr>
                <tr>
                  <td><strong>{{ $t('devices.status') }}:</strong></td>
                  <td>{{ $t('status.' + selectedDevice.status) }}</td>
                </tr>
                <tr>
                  <td><strong>{{ $t('devices.lastConnect') }}:</strong></td>
                  <td>{{ $d(new Date(selectedDevice.updatedAt), 'short') }}</td>
                </tr>
                <tr>
                  <td><strong>{{ $t('devices.lastSeen') }}:</strong></td>
                  <td>
                    <span v-if="lastSeen">{{ $d(new Date(lastSeen), 'short') }}</span>
                    <span v-else class="has-text-grey">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="buttons is-right">
              <b-button type="is-danger" icon-left="delete" @click="confirmDeleteDevice" :title="$t('devices.confirmTooltop')" v-if="selectedDevice">
                {{ $t('devices.remove') }}
              </b-button>
            </div>
          </div>
          <div class="box mt-4">
            <h3 class="title is-5">{{ $t('devices.functions') }}</h3>
            <div v-if="deviceFunctions.length === 0" class="has-text-grey">
              {{ $t('devices.noFunctions') }}
            </div>
            <table v-else class="table is-fullwidth is-striped">
              <thead>
                <tr>
                  <th>{{ $t('devices.function') }}</th>
                  <th>{{ $t('devices.parameter') }}</th>
                  <th class="has-text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fn in deviceFunctions" :key="fn">
                  <td><code>{{ fn }}</code></td>
                  <td style="width: 50%">
                    <b-input
                      v-model="functionsParams[fn]"
                      size="is-small"
                      :placeholder="$t('devices.functionParamPlaceholder')"
                    />
                    <div v-if="functionResults[fn] !== undefined" class="mt-1">
                      <small class="has-text-grey">Last result:
                        <strong v-if="functionResults[fn] === null">{{ $t('devices.invalidFormat') }}</strong>
                        <strong v-else>{{ functionResults[fn] }}</strong>
                      </small>
                    </div>
                  </td>
                  <td class="has-text-right">
                    <b-button size="is-small" @click="callFunction(fn)" :disabled="isMQTTConnecting">
                      {{ $t('global.call') }}
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="box mt-4">
            <h3 class="title is-5">{{ $t('devices.variables') }}</h3>
            <div v-if="deviceVariables.length === 0" class="has-text-grey">
              {{ $t('devices.noVariables') }}
            </div>
            <table v-else class="table is-fullwidth is-striped">
              <thead>
                <tr>
                  <th>{{ $t('devices.variable') }}</th>
                  <th>{{ $t('devices.value') }}</th>
                  <th class="has-text-right">{{ $t('global.action') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="vr in deviceVariables" :key="vr">
                  <td><code>{{ vr }}</code></td>
                  <td>
                    <span v-if="variablesValues[vr] === null" class="has-text-danger">{{ $t('devices.invalidFormat') }}</span>
                    <span v-else-if="variablesValues[vr] !== undefined">{{ variablesValues[vr] }}</span>
                    <span v-else class="has-text-grey">—</span>
                  </td>
                  <td class="has-text-right">
                    <b-button size="is-small" @click="readVariable(vr)" :disabled="isMQTTConnecting">
                      {{ $t('global.read') }}
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="has-text-centered">
        <p class="is-size-4">{{ $t('devices.notFound') }}</p>
        <b-button @click="$router.push({name: 'device-list'})" class="mt-4">
          {{ $t('global.back') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState, mapActions } from 'vuex';
import type { MqttClient } from 'mqtt';
import { createMqttClient } from '@/services/mqtt';
import EventsStreamTable from '@/components/EventsStreamTable.vue'
import InlineNameEditor from '@/components/InlineNameEditor.vue'
import CopyToClipboard from '@/components/CopyToClipboard.vue'
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Line } from 'vue-chartjs';

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Tooltip, Legend, zoomPlugin);

type MQTTPayload = { toString: () => string };
type DeviceStatDTO = { t: string; r: number; h: number };
type DeviceDetailDTO = { lastSeen?: string };
type LineChartComponentRef = { chart?: ChartJS };

export default defineComponent({
  name: 'DeviceDetails',
  components: { Line, EventsStreamTable, CopyToClipboard, InlineNameEditor },
  inject: ['keycloak', 'isDarkMode'],
  props: ['id'],
  data() {
    return {
      mqttClient: null as MqttClient | null,
      isMQTTConnecting: false,
      events: [] as Array<{ time: string; topic: string; message: string }>,
      activeTab: 0,
      window: '1h',
      stats: [] as DeviceStatDTO[],
      statsLoading: false,
      // reactive dark mode comes from injection; keep a local accessor for types
      // isDarkMode is injected as a Ref<boolean>
      detailedDevice: null as DeviceDetailDTO | null,
      functionsParams: {} as Record<string, string>,
      functionCallIds: {} as Record<string, number | string>,
      functionResults: {} as Record<string, unknown>,
      variablesValues: {} as Record<string, unknown>,
    };
  },
  computed: {
    deviceEventsPrefix(): string {
      return this.selectedDevice ? `devices/${this.selectedDevice.id}/events/` : ''
    },
    deviceEventTopics(): string[] {
      return this.selectedDevice ? [`devices/${this.selectedDevice.id}/events/#`] : []
    },
    ...mapState(['devices']),
    selectedDevice() {
      return this.devices[this.id];
    },
    lastSeen(): string | null {
      return this.detailedDevice?.lastSeen;
    },
    deviceFunctions(): string[] {
      return (this.selectedDevice && Array.isArray(this.selectedDevice.functions)) ? this.selectedDevice.functions : [];
    },
    deviceVariables(): string[] {
      const vars = this.selectedDevice?.variables as unknown;
      if (typeof vars === 'object') return Object.keys(vars as Record<string, unknown>).sort();
      return [];
    },
    eventsTableHeight(): number {
      // Approximate header + 13 rows height in pixels
      const headerHeight = 42;
      const rowHeight = 36;
      return headerHeight + (13 * rowHeight);
    },
    rssiData() {
      return {
        datasets: [
          {
            label: 'RSSI',
            data: this.stats.data,
            borderColor: '#3273dc',
            pointRadius: 0,
            tension: 0,
            borderCapStyle: "round",
            borderJoinStyle: "round",
            spanGaps: 1000000
          }
        ]
      };
    },
    rssiOptions() {
      const color = this.isDarkMode ? '#eee' : '#444';
      const grid = { color: this.isDarkMode ? '#555' : '#eee' };
      const refs = this.$refs as { heapChart?: LineChartComponentRef; rssiChart?: LineChartComponentRef };
      return {
        parsing: {
          xAxisKey: 't',
          yAxisKey: 'r'
        },
        responsive: true,
        maintainAspectRatio: true,
        animation: false,
        scales: {
          x: { ...this.commonTimeScale, grid, ticks: { color, autoSkip: true, maxTicksLimit: 12 } },
          y: {
            title: { color, display: true, text: 'dBm' },
            max: 0,
            grid,
            ticks: { color }
          }
        },
				plugins: {
          legend: { labels: { color } },
          zoom: {
            limits: {
              x: {
                min: new Date(this.stats.from),
                max: new Date(this.stats.to),
                minRange: 60000
              }
            },
            zoom: {
              drag: { enabled: true },
              wheel: { enabled: true },
              pinch: { enabled: true },
              mode: 'x',
              onZoom: function (chart) {
                const zoomLevelX = chart.chart.getZoomedScaleBounds()?.x;
                const other = refs?.heapChart?.chart;
                if (!other || zoomLevelX?.min == undefined || zoomLevelX?.max == undefined) return;
                other.options.scales.x.min = zoomLevelX.min;
                other.options.scales.x.max = zoomLevelX.max;
                other.update('none');
              }
            }
          }
        }
      };
    },
    heapData() {
      return {
        datasets: [
          {
            label: 'Free Heap',
            data: this.stats.data,
            borderColor: '#23d160',
            backgroundColor: 'rgba(35, 209, 96, 0.25)',
            fill: true,
            pointRadius: 0,
            tension: 0,
            borderCapStyle: "round",
            borderJoinStyle: "round",
            spanGaps: 1000000
          }
        ]
      };
    },
    heapOptions() {
      const color = this.isDarkMode ? '#eee' : '#444';
      const grid = { color: this.isDarkMode ? '#555' : '#eee' };
      const refs = this.$refs as { heapChart?: LineChartComponentRef; rssiChart?: LineChartComponentRef };
      return {
        parsing: {
          xAxisKey: 't',
          yAxisKey: 'h'
        },
        responsive: true,
        maintainAspectRatio: true,
        animation: false,
        scales: {
          x: { ...this.commonTimeScale, grid, ticks: { color, autoSkip: true, maxTicksLimit: 12 } },
          y: {
            title: { color,display: true, text: 'kB' },
            min: 0,
            grid,
            ticks: {
              color,
              callback: function (value) {
                return (value / 1000).toFixed(0);
              }
            }
          }
        },
        plugins: {
          legend: { labels: { color } },
          zoom: {
            limits: {
              x: {
                min: new Date(this.stats.from),
                max: new Date(this.stats.to),
                minRange: 60000
              }
            },
            zoom: {
              drag: { enabled: true },
              wheel: { enabled: true },
              pinch: { enabled: true },
              mode: 'x',
              onZoom: function (chart) {
                const zoomLevelX = chart.chart.getZoomedScaleBounds()?.x;
                const other = refs?.rssiChart?.chart;
                if (!other || zoomLevelX?.min == undefined || zoomLevelX?.max == undefined) return;
                other.options.scales.x.min = zoomLevelX.min;
                other.options.scales.x.max = zoomLevelX.max;
                other.update('none');              }
            }
          }
        }
      };
    },
    commonTimeScale() {
      return {
        type: 'time',
        time: {
          minUnit: 'minute',
          displayFormats: {
            hour: 'HH:mm',
            minute: 'HH:mm',
            day: 'MMM d'
          }
        },
        ticks: { autoSkip: true, maxTicksLimit: 6 },
        min: this.stats.from,
        max: this.stats.to
      };
    },
  },
  methods: {
    ...mapActions(['loadModel', 'updateDevice']),
    async confirmDeleteDevice() {
      const vm = this as any
      const proxy = vm?.$ ? vm : vm
      const msg = proxy?.$t ? proxy.$t('devices.confirmRemove', { id: this.selectedDevice?.name || this.selectedDevice?.id }) : `Delete device ${this.selectedDevice?.id}?`
      const confirmText = proxy?.$t ? proxy.$t('global.delete') : 'Delete'
      const cancelText = proxy?.$t ? proxy.$t('global.cancel') : 'Cancel'
      proxy?.$buefy?.dialog?.confirm?.({
        message: msg,
        type: 'is-danger',
        confirmText,
        cancelText,
        hasIcon: true,
        onConfirm: async () => {
          try {
            await (this as any).$store.dispatch('deleteDevice', { id: this.selectedDevice.id })
            this.$router.push({ name: 'device-list' })
          } catch (_e) {
            // optional toast on error
            proxy?.$buefy?.toast?.open?.({ message: String((_e as Error)?.message || _e), type: 'is-danger' })
          }
        }
      })
    },
    async saveDeviceName(name: string) {
      if (!this.selectedDevice) return;
      const trimmed = (name || '').trim();
      if (!trimmed || trimmed === this.selectedDevice.name) return;
      try {
        await this.updateDevice({ id: this.selectedDevice.id, name: trimmed });
      } catch (_e) {
        (this as any)?.$buefy?.toast?.open?.({ message: (this as any)?.$t?.('devices.renameFailed'), type: 'is-danger' });
      }
    },
    async loadDeviceDetail() {
      if (!this.selectedDevice) return;
      try {
        const response = await fetch(`/api/v1/devices/${this.selectedDevice.id}`, {
          headers: { Authorization: `Bearer ${this.keycloak.token}` },
        });
        if (!response.ok) return;
        const data = await response.json() as DeviceDetailDTO;
        this.detailedDevice = data;
      } catch (_e) { /* ignore */ }
    },
    async loadStats() {
      if (!this.selectedDevice) return;
      try {
        this.statsLoading = true;
        const response = await fetch(`/api/v1/devices/${this.selectedDevice.id}/stats?window=${this.window}`, {
          headers: { Authorization: `Bearer ${this.keycloak.token}` },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.stats = await response.json() as DeviceStatDTO[] || [];
      } catch (e) {
        console.error('Failed to load stats', (e as Error).message);
      } finally {
        this.statsLoading = false;
      }
    },
    async ensureMQTTConnected() {
      if (this.mqttClient) return true;
      this.isMQTTConnecting = true;
      this.mqttClient = await createMqttClient({ clientIdPrefix: 'console-app-device-details', reconnectPeriodMs: 1000 });
      if (!this.mqttClient) { this.isMQTTConnecting = false; return false; }

      const baseTopic = `devices/${this.selectedDevice.id}`;

      this.mqttClient.on('connect', () => {
        this.isMQTTConnecting = false;
        this.mqttClient.subscribe(`${baseTopic}/functions/+/result`);
        this.mqttClient.subscribe(`${baseTopic}/variables/+/value`);
      });

      this.mqttClient.on('message', (topic: string, message: MQTTPayload) => {
        // Device events are handled by EventsStreamTable component

        // Handle function results
        const fnPrefix = `${baseTopic}/functions/`;
        const resultSuffix = '/result';
        if (topic.startsWith(fnPrefix) && topic.endsWith(resultSuffix)) {
          const withoutPrefix = topic.substring(fnPrefix.length);
          const functionName = withoutPrefix.substring(0, withoutPrefix.length - resultSuffix.length);
          try {
            const payload: unknown = JSON.parse(message.toString());
            if (payload && typeof payload === 'object') {
              const obj = payload as Record<string, unknown>;
              const receivedId = (obj.i as unknown) as string | number | undefined;
              const expectedId = this.functionCallIds[functionName];
              if (expectedId !== undefined && String(receivedId) === String(expectedId)) {
                this.functionResults = { ...this.functionResults, [functionName]: obj.r as unknown };
              }
            } else {
              this.functionResults = { ...this.functionResults, [functionName]: null };
            }
          } catch (_err) {
            this.functionResults = { ...this.functionResults, [functionName]: null };
          }
          return;
        }

        // Handle variable values
        const varPrefix = `${baseTopic}/variables/`;
        const valueSuffix = '/value';
        if (topic.startsWith(varPrefix) && topic.endsWith(valueSuffix)) {
          const withoutPrefix = topic.substring(varPrefix.length);
          const variableName = withoutPrefix.substring(0, withoutPrefix.length - valueSuffix.length);
          const text = message.toString();
          try {
            const parsed: unknown = JSON.parse(text);
            if (parsed && typeof parsed === 'object') {
              const obj = parsed as Record<string, unknown>;
              this.variablesValues = { ...this.variablesValues, [variableName]: (obj.v as unknown) };
            } else {
              this.variablesValues = { ...this.variablesValues, [variableName]: null };
            }
          } catch (_err) {
            this.variablesValues = { ...this.variablesValues, [variableName]: null };
          }
        }
      });

      this.mqttClient.on('error', () => {
        this.isMQTTConnecting = false;
      });

      return true;
    },
    callFunction(functionName: string) {
      if (!this.ensureMQTTConnected()) return;
      const topic = `devices/${this.selectedDevice.id}/functions/${functionName}`;
      const callId = Date.now();
      const payload = JSON.stringify({ i: callId, p: this.functionsParams[functionName] || '' });
      this.functionCallIds = { ...this.functionCallIds, [functionName]: callId };
      this.functionResults = { ...this.functionResults, [functionName]: undefined };
      this.mqttClient.publish(topic, payload);
    },
    readVariable(variableName: string) {
      if (!this.ensureMQTTConnected()) return;
      this.variablesValues = { ...this.variablesValues, [variableName]: undefined };
      const topic = `devices/${this.selectedDevice.id}/variables/${variableName}`;
      this.mqttClient.publish(topic, "");
    },
  },
  async mounted() {
    if (!this.selectedDevice) {
      await this.loadModel({ model: 'devices' });
    }
    // Lazy connect to MQTT on mount so function results are captured
    if (this.selectedDevice) {
      this.ensureMQTTConnected();
      this.loadStats();
      this.loadDeviceDetail();
    }
    // dark mode reactivity comes from injected Ref provided in main.ts
  },
  beforeUnmount() {
    this.mqttClient?.end();
    // no dark mode listeners here; managed globally in main.ts
  },
});
</script>

<style scoped>
@media screen and (max-width: 768px) {
  .device-details {

    .box {
      padding: 0;
      background: transparent;
      box-shadow: none;
      border: 0;
      margin-left: 0;
      margin-right: 0;
    }
  }

}
</style>
